import { applyGuardrails, GuardrailResult } from './guardrails';

export interface AIRequest {
  prompt: string;
  systemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
  context?: Record<string, any>;
}

export interface AIResponse {
  content: string;
  provider: string;
  model: string;
  tokensUsed: number;
  guardrailsPassed: boolean;
  guardrailFlags?: string[];
}

interface AIProvider {
  id: string;
  provider_key: string;
  name: string;
  api_endpoint: string;
  default_model: string;
}

interface TenantAIConfig {
  id: string;
  tenant_id: string;
  provider_id: string;
  api_key: string;
  is_enabled: boolean;
  status: string;
  priority: number;
  ai_providers: AIProvider;
}

export class AIOrchestrator {
  private tenantId: string;

  constructor(tenantId: string) {
    this.tenantId = tenantId;
  }

  async execute(request: AIRequest): Promise<AIResponse> {
    const config = await this.selectProvider();

    if (!config) {
      throw new Error('No active AI provider configured for tenant');
    }

    const guardrailCheck = applyGuardrails(request.prompt, request.context);

    if (!guardrailCheck.passed) {
      throw new Error(`Guardrail check failed: ${guardrailCheck.flags.join(', ')}`);
    }

    const systemPrompt = this.buildSystemPrompt(request.systemPrompt);

    let response: AIResponse;

    try {
      switch (config.ai_providers.provider_key) {
        case 'openai':
          response = await this.callOpenAI(config, request, systemPrompt);
          break;
        case 'anthropic':
          response = await this.callAnthropic(config, request, systemPrompt);
          break;
        case 'groq':
          response = await this.callGroq(config, request, systemPrompt);
          break;
        case 'mistral':
          response = await this.callMistral(config, request, systemPrompt);
          break;
        default:
          throw new Error(`Unsupported provider: ${config.ai_providers.provider_key}`);
      }

      response.guardrailsPassed = true;
      response.guardrailFlags = guardrailCheck.flags;

      await this.logUsage(config, request, response);

      return response;
    } catch (error: any) {
      console.error('AI execution error:', error);
      throw new Error(`AI execution failed: ${error.message}`);
    }
  }

  private async selectProvider(): Promise<TenantAIConfig | null> {
    const { data, error } = await supabase
      .from('tenant_ai_configurations')
      .select('*, ai_providers(*)')
      .eq('tenant_id', this.tenantId)
      .eq('is_enabled', true)
      .eq('status', 'active')
      .order('priority', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error selecting provider:', error);
      return null;
    }

    return data as TenantAIConfig | null;
  }

  private buildSystemPrompt(customPrompt?: string): string {
    let basePrompt = `You are an AI assistant for a Food & Beverage SaaS platform.
You help with inventory management, procurement, and warehouse operations.
Provide clear, concise, and actionable responses.
Do not include sensitive information in your responses.
Follow food safety and industry best practices.`;

    if (customPrompt) {
      return `${basePrompt}\n\n${customPrompt}`;
    }

    return basePrompt;
  }

  private buildFinancialPrompt(mode: string): string {
    const modePrompts: Record<string, string> = {
      cashflow: 'You are a financial AI analyzing cashflow. Provide predictions, risk periods, and actionable recommendations.',
      profitability: 'You are a financial AI analyzing profitability. Identify trends, anomalies, and optimization opportunities.',
      spending: 'You are a financial AI analyzing spending patterns. Identify cost drivers, waste, and savings opportunities.',
      variance: 'You are a financial AI analyzing cost variances. Flag anomalies and provide explanations.',
      risk_detection: 'You are a financial AI detecting financial risks. Flag suspicious patterns and provide severity assessments.',
    };

    return modePrompts[mode] || this.buildSystemPrompt();
  }

  private async callOpenAI(
    config: TenantAIConfig,
    request: AIRequest,
    systemPrompt: string
  ): Promise<AIResponse> {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.api_key}`,
      },
      body: JSON.stringify({
        model: config.ai_providers.default_model || 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: request.prompt },
        ],
        temperature: request.temperature || 0.7,
        max_tokens: request.maxTokens || 500,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      content: data.choices[0]?.message?.content || '',
      provider: 'OpenAI',
      model: data.model,
      tokensUsed: data.usage?.total_tokens || 0,
      guardrailsPassed: true,
    };
  }

  private async callAnthropic(
    config: TenantAIConfig,
    request: AIRequest,
    systemPrompt: string
  ): Promise<AIResponse> {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': config.api_key,
        'anthropic-version': '2023-06-01',
      },
      body: JSON.stringify({
        model: config.ai_providers.default_model || 'claude-3-haiku-20240307',
        max_tokens: request.maxTokens || 500,
        temperature: request.temperature || 0.7,
        system: systemPrompt,
        messages: [
          { role: 'user', content: request.prompt },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Anthropic API error: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      content: data.content[0]?.text || '',
      provider: 'Anthropic',
      model: data.model,
      tokensUsed: data.usage?.input_tokens + data.usage?.output_tokens || 0,
      guardrailsPassed: true,
    };
  }

  private async callGroq(
    config: TenantAIConfig,
    request: AIRequest,
    systemPrompt: string
  ): Promise<AIResponse> {
    const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.api_key}`,
      },
      body: JSON.stringify({
        model: config.ai_providers.default_model || 'mixtral-8x7b-32768',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: request.prompt },
        ],
        temperature: request.temperature || 0.7,
        max_tokens: request.maxTokens || 500,
      }),
    });

    if (!response.ok) {
      throw new Error(`Groq API error: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      content: data.choices[0]?.message?.content || '',
      provider: 'Groq',
      model: data.model,
      tokensUsed: data.usage?.total_tokens || 0,
      guardrailsPassed: true,
    };
  }

  private async callMistral(
    config: TenantAIConfig,
    request: AIRequest,
    systemPrompt: string
  ): Promise<AIResponse> {
    const response = await fetch('https://api.mistral.ai/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.api_key}`,
      },
      body: JSON.stringify({
        model: config.ai_providers.default_model || 'mistral-small-latest',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: request.prompt },
        ],
        temperature: request.temperature || 0.7,
        max_tokens: request.maxTokens || 500,
      }),
    });

    if (!response.ok) {
      throw new Error(`Mistral API error: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      content: data.choices[0]?.message?.content || '',
      provider: 'Mistral',
      model: data.model,
      tokensUsed: data.usage?.total_tokens || 0,
      guardrailsPassed: true,
    };
  }

  private async logUsage(
    config: TenantAIConfig,
    request: AIRequest,
    response: AIResponse
  ): Promise<void> {
    try {
      await supabase.from('ai_usage_logs').insert({
        tenant_id: this.tenantId,
        provider_id: config.provider_id,
        model: response.model,
        prompt_tokens: Math.floor(response.tokensUsed * 0.4),
        completion_tokens: Math.floor(response.tokensUsed * 0.6),
        total_tokens: response.tokensUsed,
        feature_context: request.context?.feature || 'general',
        execution_time_ms: 0,
        success: true,
      });
    } catch (error) {
      console.error('Error logging AI usage:', error);
    }
  }
}

export async function executeAI(tenantId: string, request: AIRequest): Promise<AIResponse> {
  const orchestrator = new AIOrchestrator(tenantId);
  return orchestrator.execute(request);
}
